import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api, fetchKeeperCheck } from "@/lib/api";

const STORAGE_KEY = "rfg_keeper_token";

export default function KeeperLogin() {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [isKeeper, setIsKeeper] = useState(false);
  const [error, setError] = useState("");

  // Lock body scroll while the modal is open so it can't be hidden behind page scroll.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    fetchKeeperCheck()
      .then((d) => setIsKeeper(!!d.is_keeper))
      .catch(() => setIsKeeper(false));
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = token.trim();
    if (!trimmed) return;
    try {
      localStorage.setItem(STORAGE_KEY, trimmed);
      // re-check with interceptor now attaching the header
      const res = await api.get("/grove/keeper-check");
      if (res.data?.is_keeper) {
        setIsKeeper(true);
        setOpen(false);
        // Reload so sealed rounds refetch with the token attached
        window.location.reload();
      } else {
        localStorage.removeItem(STORAGE_KEY);
        setError("that token did not match. check your .env or ask the keeper.");
      }
    } catch (_e) {
      setError("could not verify token.");
    }
  }, [token]);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setIsKeeper(false);
    window.location.reload();
  }, []);

  if (isKeeper) {
    return (
      <button
        onClick={signOut}
        className="text-[10px] tracking-[0.28em] uppercase text-[#869960] hover:text-[#1C241F] transition-colors"
        data-testid="keeper-signout"
        title="sign out (sealed rounds will become opaque again)"
      >
        ✦ keeper
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[10px] tracking-[0.28em] uppercase text-[#9FA9A3] hover:text-[#1C241F] transition-colors"
        data-testid="keeper-signin-button"
        title="if you are the keeper, sign in to see sealed rounds"
      >
        sign in
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
        <div
          className="fixed inset-0 z-[100] bg-[#1C241F]/60 overflow-y-auto flex items-start justify-center px-4 pt-16 sm:pt-24 pb-8"
          onClick={() => setOpen(false)}
          data-testid="keeper-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-[#fbf8ec] border border-[#d8d3c4] rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-xl"
          >
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#6C5943] mb-2">
              ⟡ for the keeper
            </div>
            <h3 className="font-serif text-2xl text-[#1C241F] mb-2">
              sign in
            </h3>
            <p className="text-sm text-[#526357] italic mb-4">
              Paste your keeper token. Sealed rounds will then unfurl. The token lives only in this browser.
            </p>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="keeper token"
              autoFocus
              className="w-full px-4 py-3 bg-[#F4F1E8] border border-[#d8d3c4] rounded-lg text-sm font-mono text-[#1C241F] focus:outline-none focus:border-[#B85C38]"
              data-testid="keeper-token-input"
            />
            {error && (
              <p className="mt-3 text-xs text-[#B85C38]" data-testid="keeper-error">
                {error}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs text-[#526357] hover:text-[#1C241F]"
              >
                cancel
              </button>
              <button
                type="submit"
                disabled={!token.trim()}
                className="text-[10px] tracking-[0.28em] uppercase px-5 py-2 rounded-full border border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-[#F4F1E8] disabled:opacity-50"
                data-testid="keeper-submit"
              >
                sign in
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
